import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

/**
 * Admin control for one user's multi-country access and marketplace commission override.
 * Home country + extra permitted countries -> PUT /admin/users/{id}/countries
 * Commission override (blank = platform rate)    -> PUT /admin/users/{id}/commission
 */
export default function UserAccessDialog({ user, countries, onClose, onSaved }) {
  const { api } = useAuth();
  const [home, setHome] = useState(user.country || '');
  const [allowed, setAllowed] = useState(user.allowed_countries || []);
  const [commission, setCommission] = useState(user.commission_percent ?? '');
  const [saving, setSaving] = useState(false);
  const canSell = ['university_manager', 'course_provider', 'event_host'].includes(user.user_type);
  const isAdmin = user.user_type === 'admin';

  const toggle = (code) => setAllowed(a => (a.includes(code) ? a.filter(c => c !== code) : [...a, code]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/countries`, { country: home || undefined, allowed_countries: allowed });
      if (canSell) {
        await api.put(`/admin/users/${user.id}/commission`, { percent: commission === '' ? null : parseFloat(commission) });
      }
      toast.success('User access updated');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Country access &amp; commission</DialogTitle>
          <DialogDescription>{user.full_name} · {user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Home country</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={home} onChange={e => setHome(e.target.value)} data-testid="user-home-country">
              <option value="">(unchanged)</option>
              {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          {isAdmin ? (
            <p className="text-sm text-muted-foreground">Admins can always act in every country.</p>
          ) : (
            <div className="space-y-1">
              <Label>Also permitted to work in</Label>
              <div className="grid grid-cols-2 gap-1 border rounded-md p-2 max-h-48 overflow-y-auto">
                {countries.filter(c => c.code !== home).map(c => (
                  <label key={c.code} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={allowed.includes(c.code)} onChange={() => toggle(c.code)} />{c.name}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Lets this user post jobs, apply and search in these countries as well as their own.</p>
            </div>
          )}
          {canSell && (
            <div className="space-y-1">
              <Label>Commission override (%)</Label>
              <Input type="number" min={0} max={100} step="0.5" placeholder="Platform default" value={commission}
                onChange={e => setCommission(e.target.value)} data-testid="user-commission" />
              <p className="text-xs text-muted-foreground">Leave blank to use the platform commission.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#003366] hover:bg-[#003366]/90">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
